import { mapActions, mapGetters, mapState } from 'vuex'
import uuid from '@/booksy-vue-components/mixins/uuid'

const SUFFIX = 'attachable'

const ATTACHABLE_CANCEL_REASON = {
  CLICK_OUTSIDE: 'CLICK_OUTSIDE',
  BEFORE_DESTROY: 'BEFORE_DESTROY',
  ESC_CLICKED: 'ESC_CLICKED',
}

export default {

  emits: [
    'opened',
    'closed',
    'click-outside',
    'handle-escape',
  ],

  props: {

    clickOutsideCloses: {
      type: Boolean,
      default: true,
    },

    autoFocus: {
      type: Boolean,
      default: true,
    },

    autoHandleEscape: {
      type: Boolean,
      default: true,
    },

  },

  data () {
    return {
      show: false,
      attached: false,
      zIndex: null,
      currentUuid: null,
    }
  },

  mixins: [
    uuid,
  ],

  computed: {

    ...mapState({
      uuids: state => state.attachableIndex.uuids,
    }),

    ...mapGetters({
      maxIndex: 'attachableIndex/getMaxIndex',
    }),

    cssStyle () {
      if (this.zIndex) {
        return `z-index: ${this.zIndex}`
      }
      return null
    },

  },

  methods: {

    ...mapActions({
      attachableIndexOpen: 'attachableIndex/open',
      attachableIndexClose: 'attachableIndex/close',
      attachableIndexDestroy: 'attachableIndex/destroy',
    }),

    _show (value) {
      this.show = !!value
      if (this.show) {
        this.$nextTick(() => {
          this._focus()
          this.$emit('opened')
        })
      } else {
        this.$emit('closed')
      }
    },

    _clickOutside () {
      this.$emit('click-outside')
      if (this.clickOutsideCloses) {
        return this.cancel(this.cancelMessage(ATTACHABLE_CANCEL_REASON.CLICK_OUTSIDE))
      }
    },

    /*
     * focus first input element when value of auto-focus prop is true
     */
    _focus () {
      const input = this.$el.querySelector('input[type=text], input[type=search], textarea[type=text]')
      if (input && this.autoFocus) input.focus()
    },

    _handleKeyUp (e) {
      if (this.maxIndex === this.zIndex) {
        if (e.keyCode === 27 && this.clickOutsideCloses) {
          if (this.autoHandleEscape) {
            this.cancel(this.cancelMessage(ATTACHABLE_CANCEL_REASON.ESC_CLICKED))
          } else {
            this.$emit('handle-escape')
          }
        }
      }
    },

    /*
     * returns Promise, which will be fulfilled in close method
     * or reject in cancel method
     */
    async open () {
      document.addEventListener('keyup', this._handleKeyUp)

      if (!this.attached) { this.attach() }

      if (!this.currentUuid) this.currentUuid = this.uuid(SUFFIX)
      this.zIndex = await this.attachableIndexOpen(this.currentUuid)

      this._show(true)

      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },

    close (...args) {
      document.removeEventListener('keyup', this._handleKeyUp)

      this._show(false)

      this.attachableIndexClose(this.currentUuid)

      if (typeof this.resolve === 'function') {
        return this.resolve(...args)
      }
    },

    cancel (...args) {
      this._show(false)

      this.attachableIndexClose(this.currentUuid)

      if (typeof this.reject === 'function') {
        // prevent from rejecting with undefined
        return args.length ? this.reject(...args) : this.reject(this.cancelMessage())
      }
    },

    toggle () {
      if (this.show) {
        this.close()
        return
      }
      return this.open()
    },

    attach () {
      const attachable = document.querySelector('#attach-elements')
      attachable.appendChild(this.$el)
      this.attached = true
    },

    detach () {
      if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
      this.attached = false
      this.currentUuid = null
    },

    cancelMessage (reason) {
      return `[attachable] closed${reason ? ` with reason: ${reason}` : ''}`
    },
  },

  beforeUnmount () {
    if (this.show) {
      this.cancel(this.cancelMessage(ATTACHABLE_CANCEL_REASON.BEFORE_DESTROY))
    }

    this.attachableIndexDestroy()
    this.detach()
  },
}
